import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

// Components
import ModalContainer from '../../../../../components/modals/containers/ModalContainer';

// Types
import { DashboardWidgetCatalogEntry } from '../../registry';

// Styled Components
import {
  Container,
  SearchInput,
  WidgetList,
  WidgetItem,
  EmptyState,
  AddButton,
} from './styles';
import { matchesSearch } from '../../../../../utils/textSearch';

interface NewWidgetModalProps {
  onClickClose: () => void;
  availableWidgets: DashboardWidgetCatalogEntry[];
  onAddWidget: (id: string) => void;
}

export const NewWidgetModal = ({
  onClickClose,
  availableWidgets,
  onAddWidget,
}: NewWidgetModalProps): JSX.Element => {
  const { t } = useTranslation(['pages', 'common']);
  const theme = useTheme();
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const filteredWidgets = useMemo(() => {
    if (!search.trim()) return availableWidgets;
    return availableWidgets.filter((w) => matchesSearch(t(w.i18nKey), search));
  }, [search, availableWidgets, t]);

  const handleAdd = () => {
    if (selectedId) {
      onAddWidget(selectedId);
      onClickClose();
    }
  };

  const isEmpty = availableWidgets.length === 0;
  const noMatch = filteredWidgets.length === 0 && !isEmpty;

  return (
    <ModalContainer
      width={40}
      onClickClose={onClickClose}
      fill={theme.colors.black}
      title={t('pages:dashboard.newWidget.title')}
    >
      <Container>
        {isEmpty ? (
          <EmptyState>
            {t('pages:dashboard.newWidget.empty')}
          </EmptyState>
        ) : (
          <>
            <SearchInput
              type="text"
              placeholder={t('buttons:search')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {noMatch ? (
              <EmptyState>
                {t('pages:dashboard.newWidget.empty')}
              </EmptyState>
            ) : (
              <WidgetList>
                {filteredWidgets.map((widget) => (
                  <WidgetItem
                    key={widget.id}
                    $isSelected={selectedId === widget.id}
                    onClick={() => setSelectedId(widget.id)}
                  >
                    {t(widget.i18nKey)}
                  </WidgetItem>
                ))}
              </WidgetList>
            )}
            <AddButton
              type="button"
              onClick={handleAdd}
              disabled={!selectedId}
            >
              {t('pages:dashboard.newWidget.add')}
            </AddButton>
          </>
        )}
      </Container>
    </ModalContainer>
  );
};
